import React, { useState, useRef, useMemo, useEffect } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import {
  Upload,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RotateCw,
  Download,
  File as FileIcon,
  ChevronLeft,
  ChevronRight,
  Layers,
  Wifi,
  WifiOff,
  DownloadCloud,
  X,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { compareFilesLocal } from '@/lib/compareEngine';
import { exportExcelReport, exportPdfReport } from '@/lib/reportExport';

const ComparisonDashboard = () => {
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [fileAPageCount, setFileAPageCount] = useState(0);
  const [fileBPageCount, setFileBPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [dragOverA, setDragOverA] = useState(false);
  const [dragOverB, setDragOverB] = useState(false);
  const [overlayOpacity, setOverlayOpacity] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [installBannerDismissed, setInstallBannerDismissed] = useState(
    () => (typeof localStorage !== 'undefined' && localStorage.getItem('installBannerDismissed') === '1')
  );
  const [isStandalone, setIsStandalone] = useState(false);

  const fileInputARef = useRef(null);
  const fileInputBRef = useRef(null);
  const transformRef = useRef(null);

  const currentPage = pages[pageIndex] || null;
  const hasComparison = pages.length > 0;

  // Online/offline detection
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // PWA install prompt capture
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const isSA =
      window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(!!isSA || window.navigator.standalone === true);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Purge any in-memory upload state on page hide so files never linger across sessions
  useEffect(() => {
    const clearAll = () => {
      setFileA(null);
      setFileB(null);
      setPages([]);
    };
    window.addEventListener('pagehide', clearAll);
    return () => window.removeEventListener('pagehide', clearAll);
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const { outcome } = await installPromptEvent.userChoice;
    if (outcome === 'accepted') toast.success('App installed');
    setInstallPromptEvent(null);
  };

  const dismissInstallBanner = () => {
    localStorage.setItem('installBannerDismissed', '1');
    setInstallBannerDismissed(true);
  };

  const handleFileSelect = (file, setFile, fileType) => {
    if (!file) return;
    if (!file.name.match(/\.(pdf|tiff?|png|jpe?g|webp)$/i)) {
      toast.error('Invalid file type. Please upload PDF or TIFF files.');
      return;
    }
    setFile(file);
    toast.success(`File ${fileType} loaded`);
  };

  const handleDragOver = (e, setDragOver) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = (e, setDragOver) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = (e, setFile, fileType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverA(false);
    setDragOverB(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileSelect(files[0], setFile, fileType);
  };

  const compareFiles = async () => {
    if (!fileA || !fileB) {
      toast.error('Please upload both files before comparing');
      return;
    }
    setLoading(true);
    setProgressMsg('Reading files…');
    try {
      const result = await compareFilesLocal(fileA, fileB, (p) => {
        if (p && p.message) setProgressMsg(p.message);
      });
      setPages(result.pages || []);
      setPageIndex(0);
      setRotation(0);
      setOverlayOpacity(100);
      setFileAPageCount(result.file_a_pages || 0);
      setFileBPageCount(result.file_b_pages || 0);
      toast.success(
        `Comparison completed — ${result.page_count} page${result.page_count > 1 ? 's' : ''} analyzed`
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Comparison error:', error);
      toast.error(error?.message || 'Failed to compare files.');
    } finally {
      setLoading(false);
      setProgressMsg('');
    }
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const downloadComparison = async () => {
    if (!currentPage) return;
    try {
      const baseImg = await loadImage(`data:image/png;base64,${currentPage.base}`);
      const overlayImg = await loadImage(`data:image/png;base64,${currentPage.overlay}`);

      const swap = rotation % 180 !== 0;
      const w = swap ? baseImg.height : baseImg.width;
      const h = swap ? baseImg.width : baseImg.height;

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(baseImg, -baseImg.width / 2, -baseImg.height / 2);
      ctx.globalAlpha = overlayOpacity / 100;
      ctx.drawImage(overlayImg, -overlayImg.width / 2, -overlayImg.height / 2);
      ctx.restore();

      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `comparison-page-${pageIndex + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Comparison image downloaded');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to prepare download');
    }
  };

  const handleExcelExport = () => {
    if (!hasComparison) return;
    try {
      exportExcelReport(
        {
          pages,
          page_count: pages.length,
          file_a_pages: fileAPageCount,
          file_b_pages: fileBPageCount,
        },
        {
          file_a_name: fileA?.name || 'File A',
          file_b_name: fileB?.name || 'File B',
        }
      );
      toast.success('Excel report exported');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to export Excel report');
    }
  };

  const handlePdfExport = async () => {
    if (!hasComparison) return;
    setExporting(true);
    try {
      await exportPdfReport(
        {
          pages,
          page_count: pages.length,
          file_a_pages: fileAPageCount,
          file_b_pages: fileBPageCount,
        },
        {
          file_a_name: fileA?.name || 'File A',
          file_b_name: fileB?.name || 'File B',
        }
      );
      toast.success('PDF report exported');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to export PDF report');
    } finally {
      setExporting(false);
    }
  };

  const resetComparison = () => {
    setFileA(null);
    setFileB(null);
    setPages([]);
    setPageIndex(0);
    setRotation(0);
    setOverlayOpacity(100);
    setFileAPageCount(0);
    setFileBPageCount(0);
    if (fileInputARef.current) fileInputARef.current.value = '';
    if (fileInputBRef.current) fileInputBRef.current.value = '';
    toast.info('Comparison reset');
  };

  const rotateClockwise = () => setRotation((r) => (r + 90) % 360);
  const rotateCounterClockwise = () => setRotation((r) => (r + 270) % 360);

  const goToPage = (delta) => {
    const next = pageIndex + delta;
    if (next < 0 || next >= pages.length) return;
    setPageIndex(next);
    transformRef.current?.resetTransform?.();
  };

  const pageBadge = useMemo(() => {
    if (!hasComparison) return null;
    return `${pageIndex + 1} / ${pages.length}`;
  }, [pageIndex, pages, hasComparison]);

  const pageMismatch = hasComparison && fileAPageCount !== fileBPageCount;
  const currentStats = currentPage?.stats;

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-[#09090B] truncate">
              Document Overlay Comparison
            </h1>
            <p
              className="text-sm text-[#71717A] mt-1 font-medium tracking-wide uppercase"
              style={{ letterSpacing: '0.2em' }}
            >
              Offline · Local-only · PDF & TIFF
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {hasComparison && (
              <div
                data-testid="page-badge"
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E4E4E7] text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]"
              >
                <Layers className="w-3.5 h-3.5" />
                Page {pageBadge}
              </div>
            )}
            <div
              data-testid="online-status"
              title={
                isOnline
                  ? 'Online — but all processing stays on-device. No files are uploaded.'
                  : 'Offline — working from local cache'
              }
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border ${
                isOnline
                  ? 'bg-white text-[#09090B] border-[#E4E4E7]'
                  : 'bg-[#09090B] text-white border-[#09090B]'
              }`}
            >
              {isOnline ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
              {isOnline ? 'Online' : 'Offline'}
            </div>
          </div>
        </div>
        {/* Install banner */}
        {installPromptEvent && !isStandalone && !installBannerDismissed && (
          <div
            data-testid="install-banner"
            className="max-w-[1800px] mx-auto mt-3 flex flex-wrap items-center justify-between gap-3 px-4 py-2 rounded-lg bg-[#09090B] text-white text-xs sm:text-sm"
          >
            <div className="flex items-center gap-2">
              <DownloadCloud className="w-4 h-4" />
              <span className="font-medium">
                Install this app for a native, always-offline experience.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="install-button"
                onClick={handleInstallClick}
                size="sm"
                className="h-8 bg-white text-[#09090B] hover:bg-[#F4F4F5] font-bold"
              >
                Install
              </Button>
              <button
                data-testid="dismiss-install-button"
                onClick={dismissInstallBanner}
                className="h-8 px-2 text-white/70 hover:text-white"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-[1800px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Control Panel */}
          <div className="lg:col-span-3 space-y-6">
            {/* File A Upload */}
            <Card className="p-6 border-[#E4E4E7] bg-white shadow-sm">
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#09090B] mb-3">
                File A
              </label>
              <div
                data-testid="file-a-upload-zone"
                className={`upload-zone border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
                  dragOverA ? 'drag-over border-[#09090B]' : 'border-[#E4E4E7]'
                }`}
                onDragOver={(e) => handleDragOver(e, setDragOverA)}
                onDragLeave={(e) => handleDragLeave(e, setDragOverA)}
                onDrop={(e) => handleDrop(e, setFileA, 'A')}
                onClick={() => fileInputARef.current?.click()}
              >
                {fileA ? (
                  <div className="space-y-2">
                    <FileIcon className="w-8 h-8 mx-auto text-[#09090B]" />
                    <p className="text-sm font-medium text-[#09090B] truncate">{fileA.name}</p>
                    <p className="text-xs text-[#71717A]">{formatBytes(fileA.size)}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 mx-auto text-[#71717A]" />
                    <p className="text-sm font-medium text-[#09090B]">Drop file or click</p>
                    <p className="text-xs text-[#71717A]">PDF, TIFF supported</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputARef}
                type="file"
                accept=".pdf,.tiff,.tif,.png,.jpg,.jpeg,.webp"
                onChange={(e) => handleFileSelect(e.target.files[0], setFileA, 'A')}
                className="hidden"
                data-testid="file-a-input"
              />
            </Card>

            {/* File B Upload */}
            <Card className="p-6 border-[#E4E4E7] bg-white shadow-sm">
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#09090B] mb-3">
                File B
              </label>
              <div
                data-testid="file-b-upload-zone"
                className={`upload-zone border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
                  dragOverB ? 'drag-over border-[#09090B]' : 'border-[#E4E4E7]'
                }`}
                onDragOver={(e) => handleDragOver(e, setDragOverB)}
                onDragLeave={(e) => handleDragLeave(e, setDragOverB)}
                onDrop={(e) => handleDrop(e, setFileB, 'B')}
                onClick={() => fileInputBRef.current?.click()}
              >
                {fileB ? (
                  <div className="space-y-2">
                    <FileIcon className="w-8 h-8 mx-auto text-[#09090B]" />
                    <p className="text-sm font-medium text-[#09090B] truncate">{fileB.name}</p>
                    <p className="text-xs text-[#71717A]">{formatBytes(fileB.size)}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 mx-auto text-[#71717A]" />
                    <p className="text-sm font-medium text-[#09090B]">Drop file or click</p>
                    <p className="text-xs text-[#71717A]">PDF, TIFF supported</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputBRef}
                type="file"
                accept=".pdf,.tiff,.tif,.png,.jpg,.jpeg,.webp"
                onChange={(e) => handleFileSelect(e.target.files[0], setFileB, 'B')}
                className="hidden"
                data-testid="file-b-input"
              />
            </Card>

            {/* Compare Button */}
            <Button
              data-testid="compare-button"
              onClick={compareFiles}
              disabled={!fileA || !fileB || loading}
              className="w-full h-12 bg-[#09090B] hover:bg-[#18181B] text-white font-bold uppercase tracking-wider control-button"
            >
              {loading ? progressMsg || 'Processing…' : 'Compare Files'}
            </Button>

            {/* Overlay opacity slider */}
            {hasComparison && (
              <Card className="p-6 border-[#E4E4E7] bg-white shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">
                    Overlay Intensity
                  </label>
                  <span
                    data-testid="opacity-value"
                    className="text-xs font-mono font-semibold text-[#09090B]"
                  >
                    {overlayOpacity}%
                  </span>
                </div>
                <Slider
                  data-testid="opacity-slider"
                  min={0}
                  max={100}
                  step={1}
                  value={[overlayOpacity]}
                  onValueChange={(v) => setOverlayOpacity(v[0])}
                  className="w-full"
                />
                <p className="text-xs text-[#71717A] mt-3 leading-relaxed">
                  Fade diff highlights to reveal the underlying document reference.
                </p>
              </Card>
            )}

            {/* Legend + Stats */}
            <Card className="p-6 border-[#E4E4E7] bg-white shadow-sm">
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#09090B] mb-4">
                Color Legend
              </label>
              <div className="space-y-3">
                <div className="legend-item flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded bg-black border border-[#E4E4E7]" />
                    <span className="text-sm font-medium text-[#09090B]">Overlap</span>
                  </div>
                  {currentStats && (
                    <span data-testid="stat-overlap" className="text-xs font-mono text-[#71717A] tabular-nums">
                      {currentStats.overlap.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="legend-item flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded bg-[#EF4444] border border-[#E4E4E7]" />
                    <span className="text-sm font-medium text-[#09090B]">File A Only</span>
                  </div>
                  {currentStats && (
                    <span data-testid="stat-a-only" className="text-xs font-mono text-[#71717A] tabular-nums">
                      {currentStats.only_a.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="legend-item flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded bg-[#22C55E] border border-[#E4E4E7]" />
                    <span className="text-sm font-medium text-[#09090B]">File B Only</span>
                  </div>
                  {currentStats && (
                    <span data-testid="stat-b-only" className="text-xs font-mono text-[#71717A] tabular-nums">
                      {currentStats.only_b.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </Card>

            {/* Action Buttons */}
            {hasComparison && (
              <div className="space-y-3">
                <Button
                  data-testid="download-button"
                  onClick={downloadComparison}
                  variant="outline"
                  className="w-full h-11 border-[#E4E4E7] hover:bg-[#FAFAFA] font-semibold control-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Page {pageIndex + 1}
                </Button>
                <Button
                  data-testid="export-excel-button"
                  onClick={handleExcelExport}
                  variant="outline"
                  className="w-full h-11 border-[#E4E4E7] hover:bg-[#FAFAFA] font-semibold control-button"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export Excel Report
                </Button>
                <Button
                  data-testid="export-pdf-button"
                  onClick={handlePdfExport}
                  disabled={exporting}
                  variant="outline"
                  className="w-full h-11 border-[#E4E4E7] hover:bg-[#FAFAFA] font-semibold control-button"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {exporting ? 'Building PDF…' : 'Export PDF Report'}
                </Button>
                <Button
                  data-testid="reset-button"
                  onClick={resetComparison}
                  variant="outline"
                  className="w-full h-11 border-[#E4E4E7] hover:bg-[#FAFAFA] font-semibold control-button"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            )}
          </div>

          {/* Main Comparison Canvas */}
          <div className="lg:col-span-9">
            <Card
              className="relative overflow-hidden border-[#E4E4E7] bg-white shadow-sm"
              style={{ minHeight: '700px' }}
            >
              <div className="tracing-beam" />

              {hasComparison && currentPage ? (
                <div className="h-full">
                  <TransformWrapper
                    ref={transformRef}
                    initialScale={1}
                    minScale={0.5}
                    maxScale={4}
                    centerOnInit
                    key={pageIndex}
                  >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                      <>
                        <div className="absolute top-4 right-4 z-10 flex flex-wrap gap-2 justify-end">
                          <Button
                            data-testid="rotate-ccw-button"
                            onClick={rotateCounterClockwise}
                            size="icon"
                            title="Rotate 90° counter-clockwise"
                            className="bg-white/90 backdrop-blur-md hover:bg-white border border-[#E4E4E7] text-[#09090B] shadow-lg control-button"
                          >
                            <RotateCcw className="w-5 h-5" />
                          </Button>
                          <Button
                            data-testid="rotate-cw-button"
                            onClick={rotateClockwise}
                            size="icon"
                            title="Rotate 90° clockwise"
                            className="bg-white/90 backdrop-blur-md hover:bg-white border border-[#E4E4E7] text-[#09090B] shadow-lg control-button"
                          >
                            <RotateCw className="w-5 h-5" />
                          </Button>
                          <div className="w-px h-9 bg-[#E4E4E7] mx-1" />
                          <Button
                            data-testid="zoom-in-button"
                            onClick={() => zoomIn()}
                            size="icon"
                            title="Zoom in"
                            className="bg-white/90 backdrop-blur-md hover:bg-white border border-[#E4E4E7] text-[#09090B] shadow-lg control-button"
                          >
                            <ZoomIn className="w-5 h-5" />
                          </Button>
                          <Button
                            data-testid="zoom-out-button"
                            onClick={() => zoomOut()}
                            size="icon"
                            title="Zoom out"
                            className="bg-white/90 backdrop-blur-md hover:bg-white border border-[#E4E4E7] text-[#09090B] shadow-lg control-button"
                          >
                            <ZoomOut className="w-5 h-5" />
                          </Button>
                          <Button
                            data-testid="zoom-reset-button"
                            onClick={() => resetTransform()}
                            size="icon"
                            title="Reset zoom"
                            className="bg-white/90 backdrop-blur-md hover:bg-white border border-[#E4E4E7] text-[#09090B] shadow-lg control-button"
                          >
                            <RotateCcw className="w-5 h-5 opacity-60" />
                          </Button>
                        </div>

                        <div
                          data-testid="rotation-badge"
                          className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md border border-[#E4E4E7] text-xs font-mono font-semibold text-[#09090B] shadow-sm"
                        >
                          {rotation}°
                        </div>

                        <TransformComponent
                          wrapperStyle={{ width: '100%', height: '700px' }}
                          contentStyle={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            data-testid="comparison-canvas"
                            style={{
                              position: 'relative',
                              transform: `rotate(${rotation}deg)`,
                              transition: 'transform 0.35s ease',
                              display: 'inline-block',
                              lineHeight: 0,
                            }}
                          >
                            <img
                              data-testid="base-image"
                              src={`data:image/png;base64,${currentPage.base}`}
                              alt="Document reference"
                              className="max-w-full max-h-[680px] object-contain block"
                              draggable={false}
                            />
                            <img
                              data-testid="comparison-result-image"
                              src={`data:image/png;base64,${currentPage.overlay}`}
                              alt="Comparison overlay"
                              style={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                opacity: overlayOpacity / 100,
                                transition: 'opacity 0.15s ease',
                                pointerEvents: 'none',
                              }}
                              draggable={false}
                            />
                          </div>
                        </TransformComponent>

                        {pages.length > 1 && (
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 px-4 py-2 rounded-full bg-white/95 backdrop-blur-md border border-[#E4E4E7] shadow-lg">
                            <Button
                              data-testid="prev-page-button"
                              onClick={() => goToPage(-1)}
                              disabled={pageIndex === 0}
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 rounded-full"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B] tabular-nums">
                              Page {pageIndex + 1} / {pages.length}
                            </span>
                            <Button
                              data-testid="next-page-button"
                              onClick={() => goToPage(1)}
                              disabled={pageIndex === pages.length - 1}
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 rounded-full"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        )}

                        {pageMismatch && (
                          <div
                            data-testid="page-mismatch-note"
                            className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-md bg-[#FEF3C7] border border-[#FDE68A] text-[11px] font-medium text-[#92400E] shadow-sm"
                          >
                            File A has {fileAPageCount} page{fileAPageCount !== 1 ? 's' : ''}, File B has{' '}
                            {fileBPageCount}. Missing pages compared against blank.
                          </div>
                        )}
                      </>
                    )}
                  </TransformWrapper>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[700px]">
                  <div className="text-center space-y-4 max-w-md px-6">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#FAFAFA] flex items-center justify-center">
                      <FileIcon className="w-8 h-8 text-[#71717A]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#09090B] mb-2">
                        {loading ? 'Working…' : 'No Comparison Yet'}
                      </h3>
                      <p className="text-sm text-[#71717A]">
                        {loading
                          ? progressMsg || 'Preparing pages…'
                          : 'Upload two files (PDF or TIFF) and click "Compare Files". Everything runs locally on your device — nothing is uploaded.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ComparisonDashboard;
